(ns journal-app-client.views.game
  (:require [goog.object :as g]
            ["@react-three/fiber" :as react-fiber]
            ["@react-three/drei" :as react-drei]
            ["three" :as three]
            [journal-app-client.utils.interop :as interop]
            [journal-app-client.utils.react :as react]
            [reagent.core :as r]))

(def canvas (r/adapt-react-class react-fiber/Canvas))

(def double-side (.-DoubleSide three))

(def perspective-camera (r/adapt-react-class (.-PerspectiveCamera react-drei)))
(def orthographic-camera (r/adapt-react-class (.-OrthographicCamera react-drei)))
(def orbit-controls (r/adapt-react-class (.-OrbitControls react-drei)))
(def environment (r/adapt-react-class (.-Environment react-drei)))
(def html (r/adapt-react-class (.-Html react-drei)))
(def stats (r/adapt-react-class (.-Stats react-drei)))

(def drei-plane (r/adapt-react-class (.-Plane react-drei)))
(def drei-box (r/adapt-react-class (.-Box react-drei)))
(def drei-sphere (r/adapt-react-class (.-Sphere react-drei)))

(def use-frame react-fiber/useFrame)

(defn preload-dart-throw-animation []
  (.preload react-drei/useGLTF "3D/darts/DarthThrow_animation.glb"))

(defn preload-3D-objects []
  (preload-dart-throw-animation))

(defn clamp-value [min max value]
  (cond (< value min) min
        (> value max) max
        :else value))

(defn auto-move-object [ref min-val max-val anim-speed animation-forwards?]
  (set! ref.current.rotation.y
        (clamp-value min-val max-val
                     (if animation-forwards?.current
                       (+ ref.current.rotation.y anim-speed)
                       (- ref.current.rotation.y anim-speed))))
  (set! ref.current.position.x
        (clamp-value -0.05 0.025
                     (if animation-forwards?.current
                       (+ ref.current.position.x anim-speed)
                       (- ref.current.position.x anim-speed))))
  (set! ref.current.position.y
        (clamp-value 0 0.075
                     (if animation-forwards?.current
                       (+ ref.current.position.y anim-speed)
                       (- ref.current.position.y anim-speed))))
  (set! animation-forwards?.current
        (cond (<= ref.current.rotation.y min-val)
              true
              (>= ref.current.rotation.y max-val)
              false
              :else animation-forwards?.current)))

(defn angle->dartboard-position [{:keys [score multiplier inner angle]}]
  (let [little-bull-radius 0.015
        inner-single-radius (+ 0.0575 little-bull-radius)
        triple-radius (+ 0.0575 inner-single-radius)
        outer-single-radius (+ 0.04 triple-radius)
        double-radius (+ 0.04 outer-single-radius)]
    (cond (= multiplier 1)
          (if (= score 0)
            [(* 0.25 (Math/cos angle)) (* 0.25 (Math/sin angle)) 0]
            (if inner
              [(* inner-single-radius (Math/cos angle)) (* inner-single-radius (Math/sin angle)) 0]
              [(* outer-single-radius (Math/cos angle)) (* outer-single-radius (Math/sin angle)) 0]))
          (= multiplier 2)
          [(* double-radius (Math/cos angle)) (* double-radius (Math/sin angle)) 0]
          (= multiplier 3)
          [(* triple-radius (Math/cos angle)) (* triple-radius (Math/sin angle)) 0]
          :else [0 0 0])))

(defn dart->dartboard-position [{:keys [score multiplier id] :as dart}]
  (let [angle-increment (/ Math/PI 10)
        little-bull-radius 0.015]
    (case score
      0 (angle->dartboard-position (assoc dart :angle (* (rem id 20) angle-increment)))
      11 (angle->dartboard-position (assoc dart :angle 0))
      8 (angle->dartboard-position (assoc dart :angle angle-increment))
      16 (angle->dartboard-position (assoc dart :angle (* 2 angle-increment)))
      7 (angle->dartboard-position (assoc dart :angle (* 3 angle-increment)))
      19 (angle->dartboard-position (assoc dart :angle (* 4 angle-increment)))
      3 (angle->dartboard-position (assoc dart :angle (* 5 angle-increment)))
      17 (angle->dartboard-position (assoc dart :angle (* 6 angle-increment)))
      2 (angle->dartboard-position (assoc dart :angle (* 7 angle-increment)))
      15 (angle->dartboard-position (assoc dart :angle (* 8 angle-increment)))
      10 (angle->dartboard-position (assoc dart :angle (* 9 angle-increment)))
      6 (angle->dartboard-position (assoc dart :angle (* 10 angle-increment)))
      13 (angle->dartboard-position (assoc dart :angle (* 11 angle-increment)))
      4 (angle->dartboard-position (assoc dart :angle (* 12 angle-increment)))
      18 (angle->dartboard-position (assoc dart :angle (* 13 angle-increment)))
      1 (angle->dartboard-position (assoc dart :angle (* 14 angle-increment)))
      20 (angle->dartboard-position (assoc dart :angle (* 15 angle-increment)))
      5 (angle->dartboard-position (assoc dart :angle (* 16 angle-increment)))
      12 (angle->dartboard-position (assoc dart :angle (* 17 angle-increment)))
      9 (angle->dartboard-position (assoc dart :angle (* 18 angle-increment)))
      14 (angle->dartboard-position (assoc dart :angle (* 19 angle-increment)))
      25 (if (= multiplier 1)
           [little-bull-radius 0 0]
           [0 0 0])
      [0 0 0])))

(defn angle->dart-arrow-position [{:keys [little-bull-radius score multiplier inner angle current-dartboard-position]}]
  (let [little-bull-radius (or little-bull-radius 0.0125)
        inner-single-radius (+ 0.0575 little-bull-radius)
        triple-radius (+ 0.0575 inner-single-radius)
        outer-single-radius (+ 0.04 triple-radius)
        double-radius (+ 0.04 outer-single-radius)]
    (cond (= multiplier 1)
          (if (= score 0)
            [(- (first current-dartboard-position) (* 0.25 (Math/cos angle)))
             (- (second current-dartboard-position) (* 0.25 (Math/sin angle)))
             0.07438973]
            (if inner
              [(- (first current-dartboard-position) (* inner-single-radius (Math/cos angle)))
               (- (second current-dartboard-position) (* inner-single-radius (Math/sin angle)))
               0.07438973]
              [(- (first current-dartboard-position) (* outer-single-radius (Math/cos angle)))
               (- (second current-dartboard-position) (* outer-single-radius (Math/sin angle)))
               0.07438973]))
          (= multiplier 2)
          [(- (first current-dartboard-position) (* double-radius (Math/cos angle)))
           (- (second current-dartboard-position) (* double-radius (Math/sin angle)))
           0.07438973]
          (= multiplier 3)
          [(- (first current-dartboard-position) (* triple-radius (Math/cos angle)))
           (- (second current-dartboard-position) (* triple-radius (Math/sin angle)))
           0.07438973]
          :else [0 0 0.07438973])))

(defn dart->dart-arrow-position [{:keys [score multiplier current-dartboard-position index inner id] :as dart}]
  (let [angle-offset (if (and (= multiplier 1) (not inner))
                       (/ Math/PI 350)
                       (/ Math/PI 500))
        angle-increment (+ (/ Math/PI 10) (cond (= index 0) angle-offset
                                                (= index 1) (- angle-offset)
                                                :else 0))
        offset 0.0025
        little-bull-radius (+ 0.0125 (when (and index (= index 0)) offset))
        dart (assoc dart :little-bull-radius little-bull-radius)]
    (case score
      0 (angle->dart-arrow-position (assoc dart :angle (* (rem id 20) angle-increment)))
      11 (angle->dart-arrow-position (assoc dart :angle 0))
      8 (angle->dart-arrow-position (assoc dart :angle angle-increment))
      16 (angle->dart-arrow-position (assoc dart :angle (* 2 angle-increment)))
      7 (angle->dart-arrow-position (assoc dart :angle (* 3 angle-increment)))
      19 (angle->dart-arrow-position (assoc dart :angle (* 4 angle-increment)))
      3 (angle->dart-arrow-position (assoc dart :angle (* 5 angle-increment)))
      17 (angle->dart-arrow-position (assoc dart :angle (* 6 angle-increment)))
      2 (angle->dart-arrow-position (assoc dart :angle (* 7 angle-increment)))
      15 (angle->dart-arrow-position (assoc dart :angle (* 8 angle-increment)))
      10 (angle->dart-arrow-position (assoc dart :angle (* 9 angle-increment)))
      6 (angle->dart-arrow-position (assoc dart :angle (* 10 angle-increment)))
      13 (angle->dart-arrow-position (assoc dart :angle (* 11 angle-increment)))
      4 (angle->dart-arrow-position (assoc dart :angle (* 12 angle-increment)))
      18 (angle->dart-arrow-position (assoc dart :angle (* 13 angle-increment)))
      1 (angle->dart-arrow-position (assoc dart :angle (* 14 angle-increment)))
      20 (angle->dart-arrow-position (assoc dart :angle (* 15 angle-increment)))
      5 (angle->dart-arrow-position (assoc dart :angle (* 16 angle-increment)))
      12 (angle->dart-arrow-position (assoc dart :angle (* 17 angle-increment)))
      9 (angle->dart-arrow-position (assoc dart :angle (* 18 angle-increment)))
      14 (angle->dart-arrow-position (assoc dart :angle (* 19 angle-increment)))
      25 (if (= multiplier 1)
           [(- (first current-dartboard-position) (if (and index (= index 0)) 0 (- little-bull-radius)))
            (- (second current-dartboard-position) (cond (= index 0) little-bull-radius
                                                         (= index 1) (- offset)
                                                         :else 0))
            0.07438973]
           [(- (first current-dartboard-position) (cond (= index 0) 0.007
                                                        (= index 1) -0.005
                                                        :else 0))
            (- (second current-dartboard-position) (cond (= index 0) 0.001
                                                         (= index 1) -0.005
                                                         :else 0))
            0.07438973])
      [0 0 0.07438973])))

(defn dart-throw-animation [darts]
  (let [dart-throw-animation-object (react-drei/useGLTF "3D/darts/DarthThrow_animation.glb")
        nodes (.-nodes dart-throw-animation-object)
        materials (.-materials dart-throw-animation-object)
        animations (.-animations dart-throw-animation-object)
        group-ref (react/use-ref nil)
        clickable-arrow-ref (react/use-ref nil)
        cam-ref (react/use-ref nil)
        animation-forwards? (react/use-ref false)
        animations (react-drei/useAnimations animations group-ref)
        actions (.-actions animations)
        [start-animation-loop? set-start-animation-loop?] (react/use-state false)
        current-move (last darts)
        [last-valid-darts set-last-valid-darts] (react/use-state darts)
        current-dart-board-position (dart->dartboard-position (last last-valid-darts))
        dart-frequencies (frequencies (reduce
                                        (fn [r v]
                                          (conj r (select-keys v [:score :inner :multiplier])))
                                        []
                                        last-valid-darts))]
    (react/use-effect
      (fn []
        (when (and current-move (not (empty? darts)))
          (let [animations (.setDuration (g/get actions "All Animations") 1)]
            (set-last-valid-darts darts)
            (.stop animations)
            (set-start-animation-loop? false)
            (set! (.-timeScale animations) 2)               ;; This controls the direction of the animation. Reverse or forward
            (set! (.-clampWhenFinished animations) true)    ;; This forces the animation to stop at the last frame instead of first
            (set! (.-repetitions animations) 1)             ;; This sets the number of times the animation should loop
            (.play animations)
            (interop/set-timeout
              {:id       :set-start-animation-loop?
               :callback (fn [] (set-start-animation-loop? true))
               :ms       1500})))
        (fn []
          (interop/set-timeout
            {:id     :set-start-animation-loop?
             :action :clear})))
      #js [current-move])
    (use-frame
      (fn [state delta]
        (let [min-val -0.025
              max-val 0.1
              anim-speed (/ delta 200)]
          (when start-animation-loop?
            (auto-move-object cam-ref min-val max-val anim-speed animation-forwards?)))))
    [:group
     [:mesh
      {:cast-shadow true :receive-shadow true
       :geometry    (.-geometry (.-Arrow_low nodes)) :material (.-Dart_Arrow materials)
       :position    (if-let [move (first (drop-last last-valid-darts))]
                      (let [more-than-one-dart-on-same-area? (> (get dart-frequencies (select-keys move [:score :inner :multiplier])) 1)]
                        (dart->dart-arrow-position (assoc move :index (when more-than-one-dart-on-same-area? 0)
                                                               :current-dartboard-position current-dart-board-position)))
                      [0 0 -1])
       :rotation    [0 0 0]}]
     [:mesh
      {:cast-shadow true :receive-shadow true
       :geometry    (.-geometry (.-Arrow_low nodes)) :material (.-Dart_Arrow materials)
       :position    (if-let [move (second (drop-last last-valid-darts))]
                      (let [more-than-one-dart-on-same-area? (> (get dart-frequencies (select-keys move [:score :inner :multiplier])) 1)]
                        (dart->dart-arrow-position (assoc move :index (when more-than-one-dart-on-same-area? 1)
                                                               :current-dartboard-position current-dart-board-position)))
                      [0 0 -1])
       :rotation    [0 0 (/ Math/PI 4)]}]
     [:group {:ref group-ref :dispose nil}
      [:mesh
       {:name           "Dartboard_low"
        :receive-shadow true
        :geometry       (.-geometry (.-Dartboard_low nodes))
        :material       (.-Dartboard materials)
        :position       current-dart-board-position}]
      [:group {:name "Arrow_controller" :position [0, 0, 1.06290042] :rotation [0.11431814, -4e-8, 0]}
       [:group {:name "Arrow_controller_front" :position
                [0, 0, -0.07157569]}
        [:mesh {:name        "Arrow_low"
                :cast-shadow true :receive-shadow true
                :geometry    (.-geometry (.-Arrow_low nodes)) :material (.-Dart_Arrow materials)
                :position    [0, 0, 0.07438973]}
         [:mesh {:name     "Trail"
                 :geometry (.-geometry (.-Trail nodes)) :material (.-Arrow_trail materials)
                 :position [0, -0.00088864, 0.06548057]}]]]
       [:group {:name     "Corona_Camera002"
                :position [-0.04253418, 0.02819169, 1.010777]
                :rotation [1.52733651, 0.00492491, 0.03865451]}
        [perspective-camera
         {:ref      cam-ref
          :name     "CameraTrack" :make-default true :far 10000 :near 0.001 :fov 63
          :rotation [(/ (- Math/PI) 2) 0, 0]}]]]]]))

(defn sphere [& [props]]
  [drei-sphere
   (merge
     {:visible  true
      :position [10, 0, 0]
      :rotation [(/ Math/PI 2) 0, 0]}
     props)
   [:meshStandardMaterial {:color "blue"}]])

(defn box [& [props]]
  [drei-box
   (merge
     {:position [0, 0, 0]
      :rotation [(/ Math/PI 3) (/ Math/PI 3), 0]}
     props)
   [:meshStandardMaterial {:color "blue" :side double-side}]])

(defn plane [& [props]]
  [drei-plane
   (merge
     {:position [0, 0, 0]
      :rotation [(/ Math/PI 3) (/ Math/PI 3), 0]}
     props)
   [:meshStandardMaterial {:color "blue" :side double-side}]])

(defn get-move []
  (let [score (rand-int 22)
        bullseye? (= score 21)
        score (if bullseye? 25 score)
        multiplier (if bullseye? (inc (rand-int 2)) (inc (rand-int 3)))]
    {:score      score
     :multiplier multiplier
     :inner      (= (rand-int 2) 1)}))

(defn score-text [props]
  (let [move (:move props)]
    [:p
     {:style (merge {:display     "flex"
                     :align-items "end"
                     :justify-content "center"
                     :text-align "center"
                     :border-bottom "solid 1px var(--primary-brand-color)"}
                    (:style props))}
     [:span.subtitle
      {:style {:margin "0" :width "25%"}}
      (or (:score move) "-")]
     [:span.help
      {:style {:margin "0" :width "25%"}}
      (case (:multiplier move)
        1 "Single"
        2 "Double"
        3 "Triple"
        "-")]]))

(defn view [props]
  (let [[turns set-turns] (react/use-state [[{:score 25 :multiplier 2}]])]
    (cljs.pprint/pprint turns)
    [:section.section
     {:style {:position      "relative"
              :margin-top    "2rem"
              :min-height    "calc(100svh - 7.5rem)"
              :border        "1px solid var(--primary-brand-color)"
              :border-radius "1rem"
              :padding       "0"
              :overflow      "hidden"
              :display "flex"
              :justify-content "center"
              :align-items "center"}}
     [:div.box
      {:style {:position "absolute"
               :top "0.5rem"
               :left "0.5rem"
               :z-index "1"
               :min-width "25ch"
               :padding "0.5rem"
               :background-color "hsla(var(--scheme-main-hsl), 0.75)"}}
      [:div.box
       {:style {:padding "0.5rem"
                :margin-bottom "1rem"
                :background-color "hsla(var(--scheme-main-hsl), 0.75)"}}
       [:h1.title.is-4
        {:style {:display "flex" :margin "0"}}
        [:span "Total score: "]
        [:span
         {:style {:flex "1" :text-align "end"}}
         (reduce
           (fn [r turn]
             (+ r (reduce
                    (fn [r move]
                      (+ r (* (:score move) (:multiplier move))))
                    0
                    turn)))
           0
           turns)]]
       [:h1.title.is-6
        {:style {:display "flex" :margin "0"}}
        [:span "Turn: "]
        [:span
         {:style {:flex "1" :text-align "end"}}
         (count turns)]]
       [:h1.title.is-6
        {:style {:display "flex"}}
        [:span "Score: "]
        [:span
         {:style {:flex "1" :text-align "end"}}
         (reduce
           (fn [r move]
             (+ r (* (:score move) (:multiplier move))))
           0
           (last turns))]]]
      [:div.box
       {:style {:margin  "0"
                :padding "0.5rem"
                :background-color "hsla(var(--scheme-main-hsl), 0.75)"}}
       [score-text
        {:move (nth (last turns) 0 nil)}]
       [score-text
        {:move (nth (last turns) 1 nil)}]
       [score-text {:move (nth (last turns) 2 nil)}]]]
     [:button.button
      {:style {:position "absolute"
               :bottom "1rem"
               :z-index "1"}
       :on-click (fn []
                   (let [move (get-move)
                         last-turn (last turns)]
                     (set-turns
                       (if (>= (count last-turn) 3)
                         (conj turns [move])
                         (conj (vec (drop-last turns)) (conj last-turn move))))))}
      "Throw a dart!"]
     [canvas
      {:shadows true
       :camera  {:orthographic true
                 :position     [0 0 20]}
       :style   {:position "absolute"
                 :top      "0"
                 :left     "0"}}
      [environment {:files "3D/hdri/sandsloot_4k.hdr" :background false}]
      [:spotLight {:position    [1, 1, 1]
                   :angle       0.15
                   :penumbra    1
                   :intensity   0.75
                   :cast-shadow true
                   :shadow-bias -0.0001}]
      [:spotLight {:position    [-1, 1, 1]
                   :angle       0.15
                   :penumbra    1
                   :intensity   0.75
                   :cast-shadow true
                   :shadow-bias -0.0001}]
      #_[perspective-camera
         {:make-default true :far 10000 :near 0.001 :fov 45
          :position     [0 0 20]}]
      #_[orthographic-camera
         {:make-default true
          :far          10000 :near 0.001
          :zoom         1
          :top          200
          :bottom       -200
          :left         200
          :right        -200
          :position     [0 0 200]}]
      #_[orbit-controls]
      (when goog.DEBUG
        [stats])
      #_[sphere]
      #_[box
         {:scale    [5 5 5]
          :position [0, 0, 10]
          :rotation [0 0 0]}]
      [:f> dart-throw-animation (last turns)]]]))